<template>
  <q-tab-pane name="time-tracking" class="no-padding">
    <div class="layout-padding">
      <div class="row justify-end q-mb-sm-none q-mb-md">
        <q-btn icon="save" class="q-mr-md" flat @click="handleExportTimeReport" :disable="!hasTimeEntries">
          <q-tooltip>{{ $t('export_timereport') }}</q-tooltip>
        </q-btn>

        <q-btn flat>
          {{ dateRangeHumanReadable }}
          <time-range-popover v-model="range" />
        </q-btn>

        <q-btn :label="$t('filter')" icon="filter_list" class="q-mr-md" flat>
          <q-chip v-if="filterCount > 0" color="primary" floating>{{ filterCount }}</q-chip>
          <q-popover>
            <q-list separator link>
              <q-item>
                <q-item-side icon="attach_money" />
                <q-item-main>
                  <q-select
                    v-model="filter.billed"
                    :options="billedFilterOptions"
                    :placeholder="$t('time_tracking_filter_billed')"
                    hide-underline
                    clearable
                  />
                </q-item-main>
              </q-item>
            </q-list>
          </q-popover>
        </q-btn>

        <q-select
          hide-underline
          inverted-light
          color="white"
          class="project__mode-dropdown"
          :options="listModes"
          :value="listMode"
          @change="handleListModeChanged"
        />
      </div>

      <q-infinite-scroll ref="infiniteScroll" v-show="hasTimeEntries" :handler="handleFetchNextChunk">
        <time-entries-list :time-entries="timeEntries" :showUser="showAllEntries"/>
      </q-infinite-scroll>

      <template v-if="!hasTimeEntries">
        <div class="row justify-center items-center col-grow q-mt-xl">
          <div class="text-center">
            <img src="~assets/emptystates/time_entries_empty.svg"/>

            <h6 class="q-my-md text-faded">{{ $t('no_time_entries') }}</h6>

            <q-btn
              outline
              color="primary"
              :label="$t('start_time_entry')"
              @click="handleStartTimeEntry"
            />
          </div>
        </div>
      </template>
    </div>
  </q-tab-pane>
</template>

<script>
  import { createErrorFromResponse, getErrorCodeFromResponse } from 'src/utils/error'
  import { mapActions, mapGetters } from 'vuex'
  import FileSaver from 'file-saver'
  import { TimeEntriesClient } from 'src/api/TimeEntries'
  import TimeEntriesList from 'components/TimeTracking/TimeEntriesList'
  import TimeRangePopover from 'components/Common/Inputs/TimeRangePopover.vue'
  import { currentUtcDateTime } from 'src/utils/date'
  import { date } from 'quasar'
  import isSameDay from 'date-fns/is_same_day'
  import parseDate from 'date-fns/parse'

  export default {
    props: {
      project: {
        type: Object,
        required: true
      }
    },

    data () {
      return {
        listMode: 'user',
        filter: {
          billed: null,
          startedAtStart: null,
          startedAtEnd: null
        }
      }
    },

    computed: {
      ...mapGetters(['user', 'activeTeam', 'timeEntries', 'activeTimeEntry']),

      hasTimeEntries () {
        return !!this.timeEntries.length
      },

      listModes () {
        return [
          {
            value: 'user',
            label: this.$t('time_entries_list_mode.user')
          },
          {
            value: 'all',
            label: this.$t('time_entries_list_mode.all')
          }
        ]
      },

      range: {
        get () {
          return {
            start: this.filter.startedAtStart,
            end: this.filter.startedAtEnd
          }
        },
        set ({ start, end }) {
          this.filter.startedAtStart = start
          this.filter.startedAtEnd = end
        }
      },

      dateRangeHumanReadable () {
        if (this.filter.startedAtStart === null && this.filter.startedAtEnd === null) {
          return this.$t('no_date_time_selected')
        }

        if (isSameDay(this.filter.startedAtStart, this.filter.startedAtEnd)) {
          return date.formatDate(parseDate(this.filter.startedAtStart), this.$t('date_format'))
        }

        return `${date.formatDate(parseDate(this.filter.startedAtStart), this.$t('date_format'))} - ${date.formatDate(parseDate(this.filter.startedAtEnd), this.$t('date_format'))}`
      },

      showAllEntries () {
        return this.listMode === 'all'
      },

      filterCount () {
        return this.filter.billed !== null ? 1 : 0
      },

      billedFilterOptions () {
        return [
          { label: this.$t('billed'), value: true },
          { label: this.$t('not_billed'), value: false }
        ]
      },

      filterForRequest () {
        let filter = []

        filter.push(['project_id', '=', this.project.id])
        filter.push(['running', '=', false])

        if (!this.showAllEntries) {
          filter.push(['user_id', '=', this.user.id])
        }

        if (this.filter.billed !== null) {
          filter.push(['billed', '=', this.filter.billed])
        }

        if (this.filter.startedAtStart !== null) {
          filter.push(['started_at', '>=', date.formatDate(parseDate(this.filter.startedAtStart), 'YYYY-MM-DD')])
        }

        if (this.filter.startedAtEnd !== null) {
          filter.push(['started_at', '<=', date.formatDate(parseDate(this.filter.startedAtEnd), 'YYYY-MM-DD')])
        }

        return filter.filter(Boolean)
      }
    },

    methods: {
      ...mapActions([
        'createTimeEntry',
        'listTimeEntries',
        'fetchNextTimeEntriesChunk',
        'updateTimeEntry',
        'fetchActiveTimeEntry'
      ]),

      handleStartTimeEntry () {
        this.createTimeEntry({
          project_id: this.project.id,
          customer_id: this.project.customer_id && this.project.customer ? this.project.customer_id : null,
          started_at: date.formatDate(currentUtcDateTime(), 'YYYY-MM-DD HH:mm:ss')
        })
          .then(() => {
            this.$router.push({ name: 'time-tracking', params: { teamId: this.activeTeam.id } })
          })
          .catch(response => {
            const error = getErrorCodeFromResponse(response)

            if (error !== 'ACTIVE_TIME_ENTRY_EXISTS') {
              return createErrorFromResponse(response)
            }

            this.$q.dialog({
              title: this.$t('time_entry_running'),
              message: this.$t('time_entry_running_question'),
              ok: this.$t('start_new_time_entry'),
              cancel: this.$t('abort')
            })
              .then(this.fetchActiveTimeEntry)
              .then(this.stopRunningTimeEntry)
              .then(this.handleStartTimeEntry)
          })
      },

      handleFetchNextChunk (index, done) {
        this.fetchNextTimeEntriesChunk({
          done: done,
          pagination: {
            filterBy: this.filterForRequest
          }
        })
      },

      handleListModeChanged (newValue) {
        this.handleRequest()
          .then(() => {
            this.$nextTick(() => {
              this.listMode = newValue

              this.$refs.infiniteScroll.reset()
              this.$refs.infiniteScroll.resume()
            })
          })
      },

      handleRequest () {
        return this.listTimeEntries({
          pagination: {
            filterBy: this.filterForRequest
          }
        })
      },

      stopRunningTimeEntry () {
        return this.updateTimeEntry({
          timeEntryId: this.activeTimeEntry.id,
          timeEntry: {
            stopped_at: date.formatDate(currentUtcDateTime(), 'YYYY-MM-DD HH:mm:ss')
          }
        })
      },

      handleExportTimeReport () {
        let filter = {}

        if (!this.showAllEntries) {
          filter['user_id'] = [this.user.id]
        }

        if (this.filter.billed !== null) {
          filter['billed'] = this.filter.billed
        }

        if (this.filter.startedAtStart !== null) {
          filter['start_date'] = date.formatDate(parseDate(this.filter.startedAtStart), 'YYYY-MM-DD')
        }

        if (this.filter.startedAtEnd !== null) {
          filter['end_date'] = date.formatDate(parseDate(this.filter.startedAtEnd), 'YYYY-MM-DD')
        }

        filter['project_id'] = [this.project.id]

        new TimeEntriesClient()
          .export(filter)
          .then(response => {
            const pdf = response.data
            const blob = new Blob([pdf], { type: 'application/pdf' })
            FileSaver.saveAs(blob, 'report.pdf')
          })
          .catch(createErrorFromResponse)
      }
    },

    watch: {
      filter: {
        handler: function () {
          this.handleRequest()
        },
        deep: true
      }
    },

    components: {
      TimeEntriesList,
      TimeRangePopover
    }
  }
</script>

<style lang="stylus">
  @import '~variables'

  .project__mode-dropdown
    color: $faded !important
</style>
